import { useSearchParams } from 'react-router-dom';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Notice } from '@/components/feedback';
import { DetailList } from '@/components/banking';
import { resolveContext } from '@/state/session';
import { useToast } from '@/components/feedback/Toast';

/**
 * Mobile-to-web handoff — WF-SMEM-11.
 *
 * Package rule: "When a mobile workflow intentionally hands off to SME Web,
 * explain why and preserve enough context to continue the task."
 *
 * The handoff therefore always states the reason, shows exactly what context
 * travels with it, and carries company + task identifiers in the link. It never
 * silently dumps the user on a desktop home page.
 */

const WEB_ORIGIN = 'https://web.tribe-demo.example';

/** Tasks that are deliberately not reproduced on a phone, with the reason. */
const HANDOFF_REASONS: Readonly<Record<string, { title: string; why: string; path: string }>> = {
  'bulk-payments': {
    title: 'Bulk payment upload',
    why: 'Reviewing hundreds of rows, fixing individual errors and re-uploading needs a wide table, so it stays on the web app.',
    path: '/payments/bulk',
  },
  'approval-policies': {
    title: 'Approval policies',
    why: 'Approval rules span thresholds, roles and accounts at once. Editing them safely needs the full matrix view.',
    path: '/settings/approval-policies',
  },
  'team-permissions': {
    title: 'Team permissions',
    why: 'Granting permissions across many users and accounts is a dense administration task, so it stays on the web app.',
    path: '/team',
  },
  'exports': {
    title: 'Detailed exports',
    why: 'Large date-range exports and custom column selection are easier on a bigger screen.',
    path: '/reports',
  },
  default: {
    title: 'The full web experience',
    why: 'Some administration tasks are easier on a larger screen. Everything you can do here is also available there.',
    path: '/dashboard',
  },
};

export function ContinueOnWebPage() {
  const [params] = useSearchParams();
  const resolved = resolveContext();
  const toast = useToast();

  const task = params.get('task') ?? 'default';
  const reason = HANDOFF_REASONS[task] ?? HANDOFF_REASONS['default']!;
  const entityId = params.get('id');

  if (!resolved) return null;

  // Context travels as identifiers only — never as personal or account data in
  // the query string.
  const url = new URL(reason.path, WEB_ORIGIN);
  url.searchParams.set('company', resolved.company.id);
  if (entityId) url.searchParams.set('ref', entityId);

  return (
    <>
      <MobileHeader title="Continue on web" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <h2 className="tm-page-title">{reason.title}</h2>

        <Notice tone="info" title="Why this opens on the web app">
          {reason.why}
        </Notice>

        <section className="tm-card" aria-labelledby="handoff-context">
          <h3 className="tm-section-title" id="handoff-context">
            What carries over
          </h3>
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'Company', value: resolved.company.tradingName },
                { label: 'Signed in as', value: resolved.user.name },
                ...(entityId ? [{ label: 'Opening', value: entityId }] : []),
                {
                  label: 'Destination',
                  value: <span style={{ overflowWrap: 'anywhere' }}>{url.pathname}</span>,
                },
              ]}
            />
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 12 }}>
            You will be asked to confirm your identity on the web app. Your session here stays
            signed in.
          </p>
        </section>

        <a
          className="tm-btn tm-btn--block"
          href={url.toString()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open the web app
        </a>

        <button
          type="button"
          className="tm-btn tm-btn--secondary tm-btn--block"
          onClick={() => {
            void navigator.clipboard
              .writeText(url.toString())
              .then(() => toast.show({ message: 'Link copied. Open it on your computer.' }));
          }}
        >
          Copy link for later
        </button>
      </div>
    </>
  );
}
